// apps/landing-page/src/components/personalization/SegmentChipRow.tsx
'use client';

import { GraduationCap, BookOpen, Briefcase, type LucideIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import type { AudienceSegment } from '@/context/PreferencesContext';

interface SegmentChipRowProps {
  value: AudienceSegment | null;
  onChange: (segment: AudienceSegment) => void;
  /** Compact mode renders icon-only chips for the "change segment" affordance in step 2. */
  compact?: boolean;
}

const SEGMENTS: { value: AudienceSegment; icon: LucideIcon }[] = [
  { value: 'SISWA', icon: GraduationCap },
  { value: 'MAHASISWA', icon: BookOpen },
  { value: 'UMUM', icon: Briefcase },
];

export function SegmentChipRow({ value, onChange, compact = false }: SegmentChipRowProps) {
  const t = useTranslations('personalization');

  return (
    // Full cards go in a grid so they share a width — as inline-flex chips in a
    // wrap row each one shrink-wrapped to its own label, which on mobile stacked
    // into a ragged left-aligned column. Compact chips are icon-sized, so a wrap
    // row is still right for them.
    <div className={compact ? 'flex flex-wrap gap-2' : 'grid grid-cols-1 sm:grid-cols-3 gap-2'}>
      {SEGMENTS.map(({ value: segment, icon: Icon }) => {
        const selected = value === segment;
        return (
          <button
            key={segment}
            type="button"
            onClick={() => onChange(segment)}
            aria-pressed={selected}
            className={cn(
              'items-center gap-2 rounded-xl border text-left transition-all cursor-pointer',
              compact ? 'inline-flex px-3 py-1.5' : 'flex w-full h-full px-4 py-3',
              selected
                ? 'border-teal-600 bg-teal-50 ring-2 ring-teal-600/20'
                : 'border-slate-200 bg-white hover:border-teal-300 hover:shadow-md',
            )}
          >
            <span
              className={cn(
                'flex items-center justify-center rounded-lg shrink-0',
                compact ? 'w-6 h-6' : 'w-9 h-9',
                selected ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-600',
              )}
            >
              <Icon className={compact ? 'w-3.5 h-3.5' : 'w-5 h-5'} />
            </span>
            <span>
              <span className={cn('block font-bold', compact ? 'text-xs' : 'text-sm', selected ? 'text-teal-700' : 'text-slate-800')}>
                {t(`segments.${segment}.label`)}
              </span>
              {!compact && (
                <span className="block text-[11px] text-slate-400 mt-0.5">
                  {t(`segments.${segment}.description`)}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
